import { toast } from "@inno-ui/toast";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";

import type { SmartSchedulingSlotsPayload } from "../../api/smart-scheduling/smart-scheduling.types";

import { Sidebar } from "../../components/smart-scheduling/components/sidebar/sidebar";
import { TimeslotsVisibility } from "../../components/smart-scheduling/components/timeslots-visibility/timeslots-visibility";
import { useBookSmartSchedulingSlots } from "../../hooks/api/use-book-smart-scheduling.slots";
import { useDeclineSmartSchedulingSlots } from "../../hooks/api/use-decline-smart-scheduling-slots";
import { useGetSmartScheduling } from "../../hooks/api/use-get-smart-scheduling";
import { useGetSmartSchedulingAvailabilities } from "../../hooks/api/use-get-smart-scheduling-availabilities";
import { useValidateSmartSchedulingSlots } from "../../hooks/api/use-validate-smart-scheduling-slots";
import { HeaderFooterLayout } from "../../layouts/desktop/header-footer-layout/header-footer-layout";
import { SmartSchedulingLayout } from "../../layouts/smart-scheduling/smart-scheduling.layout";
import { buildIntervalsPayloadFromSelectedSlotIsos } from "../../utils/helpers/smart-scheduling";
import { SmartSchedulingBooked } from "./smart-scheduling-booked";
import { SmartSchedulingDeclined } from "./smart-scheduling-declined";
import styles from "./smart-scheduling.module.scss";

export function SmartScheduling() {
  const validateSmartSchedulingSlotsMutation
    = useValidateSmartSchedulingSlots();
  const {
    mutate: validateSmartSchedulingSlots,
    data: validateData,
    isPending: isValidatePending,
  } = validateSmartSchedulingSlotsMutation || {};
  const navigate = useNavigate();
  const bookSmartSchedulingSlotsMutation = useBookSmartSchedulingSlots();
  const {
    mutateAsync: bookSmartSchedulingSlots,
    data: bookData,
    isPending: isBookPending,
  } = bookSmartSchedulingSlotsMutation || {};
  const declineSmartSchedulingSlotsMutation = useDeclineSmartSchedulingSlots();
  const {
    mutateAsync: declineSmartSchedulingSlots,
    data: declineData,
    isPending: isDeclinePending,
  } = declineSmartSchedulingSlotsMutation || {};
  const queryResult = useGetSmartScheduling();
  const userAvailabilitiesQueryResult = useGetSmartSchedulingAvailabilities();
  const data = queryResult?.data;
  const isLoading = queryResult?.isLoading;
  const smartSchedulingData = data?.data;
  const userAvailabilities = userAvailabilitiesQueryResult?.data?.data;

  const [selectedSlotStarts, setSelectedSlotStarts] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedIntervalsPayload, setSelectedIntervalsPayload]
    = useState<SmartSchedulingSlotsPayload>({ intervals: [] });
  const lastValidatedPayloadRef = useRef<string>("");

  // if there's no data, redirect to 404 page
  if (!isLoading && (!smartSchedulingData || !smartSchedulingData?.timeframes?.length)) {
    navigate("/404");
  }

  const handleConfirmSelection = useCallback(async () => {
    if (
      bookSmartSchedulingSlots
      && selectedIntervalsPayload.intervals.length > 0
    ) {
      const response = await bookSmartSchedulingSlots(selectedIntervalsPayload);
      if (!response?.success) {
        toast.error({ title: response?.error });
      }
    }
  }, [selectedIntervalsPayload, bookSmartSchedulingSlots]);

  const handleDeclineSelection = useCallback(async () => {
    if (declineSmartSchedulingSlots) {
      const response = await declineSmartSchedulingSlots();
      if (!response?.success) {
        toast.error({ title: response?.error });
      }
    }
  }, [declineSmartSchedulingSlots]);

  const handleToggleSlot = useCallback(
    (slotStartIso: string) => {
      setSelectedSlotStarts((prev) => {
        const next = new Set(prev);
        if (next.has(slotStartIso))
          next.delete(slotStartIso);
        else next.add(slotStartIso);

        // Update payload and validate immediately
        const newPayload = buildIntervalsPayloadFromSelectedSlotIsos(
          next,
          smartSchedulingData?.timezone,
        );
        setSelectedIntervalsPayload(newPayload);

        // Validate if there are intervals and validation function is available
        // Only validate if the payload has actually changed to prevent duplicate API calls
        const payloadString = JSON.stringify(newPayload);
        if (
          newPayload.intervals.length > 0
          && validateSmartSchedulingSlots
          && payloadString !== lastValidatedPayloadRef.current
        ) {
          lastValidatedPayloadRef.current = payloadString;
          validateSmartSchedulingSlots(newPayload);
        }
        else if (newPayload.intervals.length === 0) {
          // Reset the ref when there are no intervals so validation can happen again when slots are selected
          lastValidatedPayloadRef.current = "";
        }

        return next;
      });
    },
    [smartSchedulingData?.timezone, validateSmartSchedulingSlots],
  );

  return (
    <HeaderFooterLayout hideFooter={true}>
      <div className={styles.smartSchedulingWrapper}>
        <div className={styles.smartSchedulingContent}>
          {!bookData?.success && !declineData?.success && (
            <SmartSchedulingLayout
              confirmDisabled={selectedIntervalsPayload.intervals.length === 0}
              isValidatePending={!!isValidatePending}
              validateData={validateData?.data}
              onConfirm={handleConfirmSelection}
              onDecline={handleDeclineSelection}
              isBookPending={!!isBookPending}
              isDeclinePending={!!isDeclinePending}
            >
              <div className={styles.smartSchedulingContentInner}>
                <Sidebar
                  isLoading={isLoading}
                  title={smartSchedulingData?.title}
                  description={smartSchedulingData?.description}
                  duration={smartSchedulingData?.duration}
                  location={smartSchedulingData?.location}
                  meetingProvider={smartSchedulingData?.meeting_provider}
                  participants={smartSchedulingData?.participant_count}
                  timezone={smartSchedulingData?.timezone}
                />
                <TimeslotsVisibility
                  apiTimezone={smartSchedulingData?.timezone}
                  timeframes={smartSchedulingData?.timeframes ?? []}
                  availabilities={userAvailabilities ?? []}
                  isLoading={isLoading}
                  selectedSlotStarts={selectedSlotStarts}
                  onToggleSlot={handleToggleSlot}
                />
              </div>
            </SmartSchedulingLayout>
          )}
          {!!declineData && declineData?.success && (<SmartSchedulingDeclined />)}
          {!!bookData && bookData?.success && (<SmartSchedulingBooked />)}
        </div>
      </div>
    </HeaderFooterLayout>
  );
}
